import React, { useEffect, useMemo } from 'react';
import { useScheduleContext } from '../../../context/ScheduleContext';
import { useLocaleContext } from '../../../context/LocaleContext';
import { useStreamContext } from '../../../context/StreamContext';
import { getStreamsDates } from '../../../helpers/schedule-helper';
import { isSameAs, generateDateWithDay } from '../../../helpers/times';
import { removeDuplicates } from '../../../helpers/remove-duplicates';
import { isMobile } from 'react-device-detect';
import * as CONSTANTS from '../../../constants/common';
import { PAGES_VIEW_GRID, TABS_TYPE, PAGE_TYPE, HIDE_TYPE } from '../../../constants/page-settings/schedule';
import ScheduleFiltersTags from './ScheduleFiltersTags';
import ScheduleFiltersSearch from './ScheduleFiltersSearch';
import ScheduleFiltersHalls from './ScheduleFiltersHalls';
import { CTabs, CToggler } from '../../UI';

import './style.scss';

const ScheduleFilters = ({
    viewsList,
    hasViewsFilter = true,
    hasSearch = true,
    hasFormatFilter = true,
    hasTagsFilter = true,
}) => {
    const $locale = useLocaleContext();
    const {
        event,
        page,
        episodes,
        setSchedule,
        hasManyHalls,
        scheduleFilters,
        updateScheduleFilters,
        handleSetScheduleTimeLine,
        scheduleHallsByDate,
        isWidget,
        widgetSettings,
    } = useScheduleContext();
    const streamContext = useStreamContext();

    const tagsList = useMemo(() => {
        return removeDuplicates(episodes
            .map(({ tags }) => tags)
            .flat(),
            'id'
        );
    }, [episodes]);

    const scheduleViewsOptions = useMemo(() => {
        return viewsList?.reduce((acc, view) => {
            if (view.show) {
                const option = {
                    icon: view.value,
                    value: view.value,
                };

                acc.push(option);
            }

            return acc;
        }, []);
    }, [viewsList]);

    const EPISODES_TYPE_OPTIONS = [
        {
            label: $locale.get('participationType.hybrid'),
            value: CONSTANTS.TYPE_HYBRID,
        },
        {
            label: $locale.get('participationType.online'),
            value: CONSTANTS.TYPE_ONLINE,
        },
        {
            label: $locale.get('participationType.offline'),
            value: CONSTANTS.TYPE_OFFLINE,
        },
    ];

    const isDateFiltersShown = isWidget ? widgetSettings?.daysView === TABS_TYPE : page.settings.daysView === TABS_TYPE;

    useEffect(() => {
        if (isMobile) {
            updateScheduleFilters({ scheduleView: PAGES_VIEW_GRID });

            return;
        }

        if (!!scheduleViewsOptions?.length) {
            updateScheduleFilters({ scheduleView: scheduleViewsOptions[0].value });
        }
    }, [scheduleViewsOptions]);

    useEffect(() => {
        const streams = scheduleFilters.selectedHall
            ? streamContext.streams.filter(({ id }) => id === scheduleFilters.selectedHall)
            : streamContext.streams;

        const filteredDates = getStreamsDates(streams);

        if (!!filteredDates?.length) {
            const result = filteredDates.map((unixDate) => ({ label: generateDateWithDay(unixDate, $locale.name), value: unixDate }));
            updateScheduleFilters({
                scheduleDates: result,
                date: !!scheduleFilters.date ? scheduleFilters.date : result[0].value,
            });
        }
    }, [streamContext.streams, scheduleFilters.selectedHall]);

    useEffect(handleFilterEpisodes, [scheduleFilters, episodes]);

    function handleUpdateStateArray(key, value) {
        if (!value) {
            updateScheduleFilters({ [key]: [] });

            return;
        }

        if (!scheduleFilters[key].includes(value)) {
            updateScheduleFilters({ [key]: [... new Set([...scheduleFilters[key], value])] });

            return;
        }

        updateScheduleFilters({ [key]: scheduleFilters[key].filter((item) => item !== value) });
    }

    function handleFilterEpisodes() {
        let result = episodes;

        if ((isWidget
            ? widgetSettings?.daysView === TABS_TYPE
            : page.settings.daysView === TABS_TYPE || (scheduleFilters.scheduleView === PAGES_VIEW_GRID && !isMobile))
            && !!scheduleFilters.date
        ) {
            result = episodes.filter((episode) => isSameAs(episode.timeStart, scheduleFilters.date));
        }

        if ((isWidget ? widgetSettings?.hallsView !== PAGE_TYPE : page.settings.hallsView !== PAGE_TYPE)
            && !!scheduleFilters.selectedHall
        ) {
            result = result.filter((episode) => episode.stream.id === scheduleFilters.selectedHall);
        }

        if (scheduleFilters.episodesType !== CONSTANTS.TYPE_HYBRID) {
            result = result.filter((episode) => episode.displayParticipationType === scheduleFilters.episodesType);
        }

        if (!!scheduleFilters.selectedTags.length) {
            result = result.filter((episode) => episode.tags.some((tag) => scheduleFilters.selectedTags.includes(tag.id)));
        }

        if (!!scheduleFilters.searchQuery) {
            result = result.filter((episode) => {
                const episodeValuesSearchString = Object.values(episode)
                    .filter((value) => typeof value === 'string' && !!value)
                    .join(' ');

                const episodeMembersSearchString = episode.groups
                    .map(({ members }) => {
                        return members.map(({ member }) => {
                            return Object.values(member)
                                .filter((value) => typeof value === 'string' && !!value)
                                .join(' ')
                        });
                    })
                    .flat(2)
                    .join(' ');

                return `${episodeValuesSearchString} ${episodeMembersSearchString}`.toLowerCase().includes(scheduleFilters.searchQuery.toLowerCase());
            });
        }

        result.sort((a, b) => a.timeStart - b.timeStart);

        handleSetScheduleTimeLine(result);

        setSchedule(result);
    }

    const isScheduleViewGrid = scheduleFilters.scheduleView === PAGES_VIEW_GRID;
    const isHallsViewTabs = isWidget ? widgetSettings?.hallsView === TABS_TYPE : page.settings.hallsView === TABS_TYPE;
    const willRenderHallsFilter = page?.settings?.hallsView !== HIDE_TYPE && (isMobile || (isHallsViewTabs && !isScheduleViewGrid));
    const showViewFilters = scheduleViewsOptions?.length > 1 && hasViewsFilter && !isMobile;
    const showFormatFilter = hasFormatFilter && event.participationType === CONSTANTS.TYPE_HYBRID;

    return (
        <div className={`schedule-filters schedule-filters--view-${scheduleFilters.scheduleView}`}>
            {(showViewFilters || showFormatFilter) && (
                <div className="schedule-filters-views">
                    {showViewFilters
                        ? <CToggler
                            value={scheduleFilters.scheduleView}
                            options={scheduleViewsOptions}
                            name="scheduleView"
                            onChange={(value) => updateScheduleFilters({ scheduleView: value, selectedHall: null })}
                        />
                        : <div />
                    }
                    {showFormatFilter && (
                        <CToggler
                            value={scheduleFilters.episodesType}
                            options={EPISODES_TYPE_OPTIONS}
                            name="episodesType"
                            onChange={(value) => updateScheduleFilters({ episodesType: value })}
                        />
                    )}
                </div>
            )}
            <div className={`schedule-filters-tabs `}>
                {
                    (isDateFiltersShown && !!scheduleFilters.scheduleDates?.length) &&
                    <CTabs
                        value={scheduleFilters.date}
                        tabs={scheduleFilters.scheduleDates}
                        onChange={(value) => updateScheduleFilters({ date: value })}
                    />
                }
                <div className="schedule-filters-search">
                    {
                        hasSearch &&
                        <ScheduleFiltersSearch
                            value={scheduleFilters.searchQuery}
                            onSearch={(value) => updateScheduleFilters({ searchQuery: value })}
                        />
                    }
                    {
                        (hasTagsFilter && !!tagsList.length) &&
                        <ScheduleFiltersTags
                            tags={tagsList}
                            selectedTags={scheduleFilters.selectedTags}
                            onChange={(value) => handleUpdateStateArray('selectedTags', value)}
                            onReset={() => updateScheduleFilters({ selectedTags: [] })}
                        />
                    }
                </div>
            </div>
            {willRenderHallsFilter &&
                <ScheduleFiltersHalls
                    halls={scheduleHallsByDate}
                    selectedHall={scheduleFilters.selectedHall}
                    onChange={(value) => updateScheduleFilters({ selectedHall: value })}
                />
            }
        </div>
    )
};

export default ScheduleFilters;
